import type {ReactElement} from 'react';
import {useState} from 'react';

import type {SettlementPaymentMethod} from '@afriex/core/domain/types';

import {Button} from '@/components';
import ConfirmationModal from '@/components/Modals/ConfirmationModal';
import {settlementAccountService} from '@/services/settlementAccountService';
import {isSettlementPaymentMethodArchived} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';

type SettlementAccountArchiveButtonProps = {
  item: SettlementPaymentMethod;
  /** Called after a successful toggle so the list refreshes in place. */
  onSuccess?: () => void | Promise<void>;
};

/**
 * Archiving is a soft delete: the account keeps its row and its history, drops
 * out of the invoice form, and can be brought back. It writes the one flag
 * through PATCH rather than going via the form's validator, which would demand
 * every field for a single-field change.
 */
const SettlementAccountArchiveButton = ({
  item,
  onSuccess,
}: SettlementAccountArchiveButtonProps): ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isArchived = isSettlementPaymentMethodArchived(item);
  const label = isArchived ? 'Unarchive' : 'Archive';

  const handleConfirm = async () => {
    if (!item.id) {
      return;
    }

    setIsLoading(true);
    try {
      await settlementAccountService.updateSettlementPaymentMethodById(
        item.id,
        {meta: {...item.meta, flags: {isDeactivated: !isArchived}}},
      );
      showSuccessMessage(
        isArchived
          ? 'Settlement account restored'
          : 'Settlement account archived',
      );
      setIsOpen(false);
      await onSuccess?.();
    } catch (e) {
      showErrorMessage(
        `Could not ${label.toLowerCase()} the account: ${getErrorMessage(e, '')}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="unStyled"
        colorScheme={isArchived ? 'cyan' : 'red'}
        onClick={() => setIsOpen(true)}
        dataCy="merchant-settlement-accounts-archive"
      >
        {label}
      </Button>

      <ConfirmationModal
        isOpen={isOpen}
        title={`${label} settlement account`}
        dataCy="merchant-settlement-accounts-archive-modal"
        label={
          isArchived
            ? 'This account becomes selectable on new invoices again.'
            : 'This account stops being selectable on new invoices. Invoices already raised against it keep their copy of the details.'
        }
        confirmAction={() => void handleConfirm()}
        confirmActionLabel={label}
        cancelAction={() => setIsOpen(false)}
        cancelActionLabel="Cancel"
        loadingLabel={isArchived ? 'Restoring...' : 'Archiving...'}
        loading={isLoading}
      />
    </>
  );
};

export default SettlementAccountArchiveButton;
